import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { AdminAssignmentEntry, AdminOrganizationEntry, AssignmentScope, Group } from 'chat-shared'
import { api } from '../../api/client.js'
import {
  AdminCard,
  AdminPageHeader,
  AdminTableEmpty,
  adminTableSx,
} from '../../components/AdminUi.js'
import { AssignmentPicker } from '../../components/AssignmentPicker.js'

/** Adminbereich → Zuweisungen: Chats/Ordner mit Org- oder Gruppen-Zuweisung. */
export const AdminAssignmentsSection = () => {
  const [assignments, setAssignments] = useState<Array<AdminAssignmentEntry>>([])
  const [organizations, setOrganizations] = useState<Array<AdminOrganizationEntry>>([])
  const [groupsByOrg, setGroupsByOrg] = useState<Record<string, Array<Group>>>({})
  const [assignEditTarget, setAssignEditTarget] = useState<AdminAssignmentEntry | undefined>(undefined)

  const reloadAssignments = () => {
    api.listAdminAssignments().then(setAssignments).catch(() => setAssignments([]))
  }

  useEffect(() => {
    reloadAssignments()
    // Für die Neuzuweisung (AssignmentPicker) werden Organisationen und ihre
    // Gruppen gebraucht.
    api.listAdminOrganizations().then(setOrganizations).catch(() => setOrganizations([]))
  }, [])

  const loadGroups = (orgId: string) => {
    if (groupsByOrg[orgId]) return
    api
      .listGroups(orgId)
      .then((groups) => setGroupsByOrg((prev) => ({ ...prev, [orgId]: groups })))
      .catch(() => undefined)
  }

  const handleReassign = async (kind: 'chat' | 'folder', id: string, scope: AssignmentScope) => {
    if (kind === 'chat') await api.assignChat(id, scope)
    else await api.assignFolder(id, scope)
    setAssignEditTarget(undefined)
    reloadAssignments()
  }

  const handleUnassign = async (kind: 'chat' | 'folder', id: string) => {
    if (kind === 'chat') await api.unassignChat(id)
    else await api.unassignFolder(id)
    setAssignEditTarget(undefined)
    reloadAssignments()
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <AdminPageHeader
        title="Zuweisungen"
        description="Chats und Ordner, die für eine Organisation oder Gruppe freigegeben sind — zum Ändern eine Zeile anklicken."
      />

      <AdminCard title={`Alle Zuweisungen (${assignments.length})`}>
        <Table size="small" sx={adminTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Typ</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Eigentümer</TableCell>
              <TableCell>Zugewiesen an</TableCell>
              <TableCell>Zugriff</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((entry) => (
              <TableRow
                key={`${entry.kind}-${entry.id}`}
                hover
                onClick={() => setAssignEditTarget(entry)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{entry.kind === 'chat' ? 'Chat' : 'Ordner'}</TableCell>
                <TableCell>{entry.name}</TableCell>
                <TableCell>
                  {entry.ownerDisplayName}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {entry.ownerEmail}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={`${entry.scopeType === 'organization' ? 'Org' : 'Gruppe'}: ${entry.scopeName}`}
                  />
                </TableCell>
                <TableCell>{entry.access === 'write' ? 'Lesen & Schreiben' : 'Nur Lesen'}</TableCell>
              </TableRow>
            ))}
            {assignments.length === 0 && (
              <AdminTableEmpty colSpan={5}>Keine Zuweisungen vorhanden.</AdminTableEmpty>
            )}
          </TableBody>
        </Table>
      </AdminCard>

      <Dialog
        key={assignEditTarget ? `${assignEditTarget.kind}-${assignEditTarget.id}` : undefined}
        open={Boolean(assignEditTarget)}
        onClose={() => setAssignEditTarget(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Zuweisung „{assignEditTarget?.name}“ ({assignEditTarget?.kind === 'chat' ? 'Chat' : 'Ordner'})
        </DialogTitle>
        <DialogContent>
          {assignEditTarget && (
            <>
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button size="small" onClick={() => void handleUnassign(assignEditTarget.kind, assignEditTarget.id)}>
                    Entfernen
                  </Button>
                }
              >
                {assignEditTarget.scopeType === 'organization' ? 'Organisation' : 'Gruppe'} „{assignEditTarget.scopeName}“ (
                {assignEditTarget.access === 'write' ? 'Lesen & Schreiben' : 'Nur Lesen'})
              </Alert>
              <Typography variant="subtitle2" mb={1}>
                Neu zuweisen
              </Typography>
              <AssignmentPicker
                orgs={organizations}
                groupsByOrg={groupsByOrg}
                onLoadGroups={loadGroups}
                onSubmit={(scope) => void handleReassign(assignEditTarget.kind, assignEditTarget.id, scope)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignEditTarget(undefined)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
